import { PageSetting } from '../contexts/PagesContext';
import { InputLimits } from '../enums/InputLimits';

interface ValidationRule {
  name?: string;
  min?: number;
  limit?: number;
  pattern?: RegExp;
  error?: string;
}

export const sanitizePageSettingsForm = (form: PageSetting): string[] => {
  const errors: string[] = [];
  const colorRegex = /^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/; // Matches hex colors with 3 or 6 digits

  const numericFields: Record<string, ValidationRule> = {
    smart_phone_width: {
      name: 'Artboard (1400px+)',
    },
    desk_top_width: {
      name: 'Artboard (1920px+)',
    },
  };

  const rangeFields: Record<string, ValidationRule> = {
    password: {
      min: InputLimits.PASSWORD_LIMIT_MIN,
      limit: InputLimits.PASSWORD_LIMIT_MAX,
      error: `Password must be between ${InputLimits.PASSWORD_LIMIT_MIN} and ${InputLimits.PASSWORD_LIMIT_MAX} characters.`,
    },
  };

  const textFields: Record<string, ValidationRule> = {
    // basic
    title: {
      limit: InputLimits.TITLE_LIMIT,
      error: `Page title must be within ${InputLimits.TITLE_LIMIT} characters.`,
    },
    description: {
      limit: InputLimits.DESCRIPTION_LIMIT,
      error: `Page description must be within ${InputLimits.DESCRIPTION_LIMIT} characters.`,
    },
    keywords: {
      limit: InputLimits.KEYWORDS_LIMIT,
      error: `Page keywords must be within ${InputLimits.KEYWORDS_LIMIT} characters.`,
    },
    // scripts
    header_script: {
      limit: InputLimits.SEO_HEADER_SCRIPT,
      error: `Embed under the header tag must be within ${InputLimits.SEO_HEADER_SCRIPT} characters.`,
    },
    body_script: {
      limit: InputLimits.SEO_BODY_SCRIPT,
      error: `Embed under the body tag must be within ${InputLimits.SEO_BODY_SCRIPT} characters.`,
    },
    google_analytics_script: {
      limit: InputLimits.GA4_SCRIPT,
      error: `GA4 tracking code must be within ${InputLimits.GA4_SCRIPT} characters.`,
    },
    // color settings
    main_color: {
      pattern: colorRegex,
      error: 'Main color format in color settings is invalid.',
    },
    text_color: {
      pattern: colorRegex,
      error: 'Text color format in color settings is invalid.',
    },
    background_color: {
      pattern: colorRegex,
      error: 'Background color format in color settings is invalid.',
    },
    // auth settings
    auth_page_description: {
      limit: InputLimits.AUTH_PAGE_DESCRIPTION,
      error: `Password protection display text must be within ${InputLimits.AUTH_PAGE_DESCRIPTION} characters.`,
    },
  };

  Object.entries(textFields).forEach(([key, rule]) => {
    const value = form[key as keyof PageSetting];
    // Count string length matching PHP's strlen() behavior
    // When textarea content is sent to server, browsers convert \n to \r\n
    // So we need to count each \n as 2 characters to match PHP
    const stringValue = value ? value.toString() : '';
    const lineBreaks = (stringValue.match(/\n/g) || []).length;
    const actualLength = stringValue.length + lineBreaks;

    if (
      rule.limit !== undefined
      && typeof value === 'string'
      && actualLength > rule.limit
    ) {
      errors.push(rule.error || `${key} input is too long.`);
    }

    if (
      rule.pattern
      && typeof value === 'string'
      && !rule.pattern.test(value)
    ) {
      errors.push(rule.error || `${key} input format is invalid.`);
    }
  });

  Object.entries(numericFields).forEach(([key, rule]) => {
    const value = form[key as keyof PageSetting];

    if (typeof value === 'number') {
      if (rule.name && (value < 0 || value > InputLimits.NUMBER_LIMIT)) {
        errors.push(`${rule.name} must be between 1 and ${InputLimits.NUMBER_LIMIT}.`);
      }
    }
  });

  Object.entries(rangeFields).forEach(([key, rule]) => {
    const value = form[key as keyof PageSetting];

    if (typeof value === 'string') {
      // Count string length matching PHP's strlen() behavior
      const lineBreaks = (value.match(/\n/g) || []).length;
      const length = value.length + lineBreaks;

      if (length === 0) return; // Skip empty strings when password is not required

      if (rule.min !== undefined && length < rule.min) {
        errors.push(rule.error || `${key} must be at least ${rule.min} characters.`);
      }

      if (rule.limit !== undefined && length > rule.limit) {
        errors.push(rule.error || `${key} must be at most ${rule.limit} characters.`);
      }
    }
  });

  return errors;
};
